// src/components/NodeApplicationList.jsx

import React, { useState, useEffect } from 'react';
import { useWallet } from '../context/WalletContext';
import * as api from '../services/api';

/**
 * Component to display a list of user's existing node applications
 * With improved error handling and fallbacks
 */
const NodeApplicationList = () => {
  const { wallet } = useWallet();
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [blockchainNetworks, setBlockchainNetworks] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState(1); // Default to Ethereum

  // Fetch blockchain networks
  useEffect(() => {
    let isMounted = true;
    
    const fetchNetworks = async () => {
      try {
        const networks = await api.fetchBlockchainNetworks();
        
        if (!isMounted) return;
        
        if (networks && networks.length > 0) {
          setBlockchainNetworks(networks);
          setSelectedNetwork(networks[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch blockchain networks:', error);
      }
    };
    
    if (wallet.connected) {
      fetchNetworks();
    }
    
    return () => {
      isMounted = false;
    };
  }, [wallet.connected]);

  // Fetch applications when wallet and network are available
  useEffect(() => {
    let isMounted = true;
    
    const fetchApplications = async () => {
      if (!wallet.connected || !selectedNetwork) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Generate signature message
        const messageData = await api.generateSignatureMessage(
          wallet.address,
          selectedNetwork,
          wallet.type
        );
        
        if (!isMounted) return;
        
        // If no message generated, use empty string to avoid errors
        const messageToSign = messageData && messageData.message ? messageData.message : '';
        
        // Sign the message with the wallet
        let signature = '';
        try {
          signature = await api.signMessage(
            wallet.type,
            wallet.address,
            messageToSign
          );
        } catch (signError) {
          console.error('Error signing message:', signError);
          // Continue with empty signature
        }
        
        if (!isMounted) return;
        
        // Get applications with signed message
        const applicationsData = await api.getWalletApplications(
          wallet.address,
          signature,
          messageToSign,
          selectedNetwork,
          wallet.type
        );
        
        if (!isMounted) return;
        
        // Ensure we have an array of applications
        if (applicationsData && applicationsData.applications) {
          setApplications(applicationsData.applications);
        } else {
          setApplications([]);
        }
      } catch (error) {
        console.error('Error fetching applications:', error);
        
        if (isMounted) {
          setError(error.message || 'Failed to load your applications. Please try again.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    fetchApplications();
    
    return () => {
      isMounted = false;
    };
  }, [wallet.connected, wallet.address, wallet.type, selectedNetwork]);

  // Format date string
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };
  
  // Get status badge class
  const getStatusClass = (status) => {
    const statusMap = {
      'submitted': 'submitted',
      'pending_payment': 'pending_payment',
      'processing': 'processing',
      'approved': 'approved',
      'rejected': 'rejected',
      'active': 'approved',
      'suspended': 'rejected',
      'terminated': 'rejected'
    };
    
    return statusMap[status] || '';
  };

  if (!wallet.connected) {
    return null; // Don't show anything if wallet not connected
  }

  return (
    <div className="application-list">
      <h3 className="application-list-heading">
        Your Applications
        {applications.length > 0 && (
          <span className="count-badge">{applications.length}</span>
        )}
      </h3>
      
      {isLoading ? (
        <div className="applications-loading">
          <span className="spinner"></span>
          <p>Loading your applications...</p>
        </div>
      ) : error ? (
        <div className="applications-error">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
            <path d="M12 8V12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 16H12.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <p>{error}</p>
        </div>
      ) : applications.length === 0 ? (
        <div className="no-applications">
          <p>You don't have any applications yet. Apply to become a node operator below.</p>
        </div>
      ) : (
        <>
          {applications.map((app) => (
            <div 
              className="application-card" 
              key={app.id || app.reference_code || Math.random().toString(36).substring(2, 9)}
              data-reference={app.reference_code}
            >
              <div className="application-card-header">
                <div className="application-card-id">
                  Ref: {app.reference_code || 'Pending'}
                </div>
                <div className={`application-card-status ${getStatusClass(app.status)}`}>
                  {(app.status || 'pending').replace('_', ' ')}
                </div>
              </div>
              
              <div className="application-card-details">
                <div className="application-detail-item">
                  <span className="detail-label">Tier:</span>
                  <span>{app.tier_name || app.tier || 'Standard Node'}</span>
                </div>
                <div className="application-detail-item">
                  <span className="detail-label">Date Applied:</span>
                  <span>{formatDate(app.created_at)}</span>
                </div>
                <div className="application-detail-item">
                  <span className="detail-label">Last Updated:</span>
                  <span>{formatDate(app.updated_at)}</span>
                </div>
                <div className="application-detail-item">
                  <span className="detail-label">Payment Status:</span>
                  <span>{app.payment_confirmed ? 'Confirmed' : 'Pending'}</span>
                </div>
              </div>
              
              {app.status === 'pending_payment' && (
                <div className="application-actions">
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      // Future implementation for payment submission
                      alert('Payment functionality will be implemented in the next update.');
                    }}
                  >
                    Submit Payment
                  </button>
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default NodeApplicationList;
